import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';
import { EmptyState, ErrorState } from './States';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  /** Hide on small screens to keep tables readable on phones. */
  hideOnMobile?: boolean;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  footer?: ReactNode;
}

export function DataTable<T>({ columns, rows, rowKey, loading, error, onRetry, onRowClick, empty, footer }: Props<T>) {
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  return (
    <div className="relative overflow-x-auto">
      <table className="w-full border-collapse text-start whitespace-nowrap">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={cn('border-b border-[#eef1f5] bg-soft px-3 py-3 text-start text-[11px] font-medium text-[#8390a3]', c.hideOnMobile && 'max-md:hidden', c.className)}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows?.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn('border-b border-[#eef1f5] last:border-0', onRowClick && 'cursor-pointer hover:bg-soft')}
            >
              {columns.map((c) => (
                <td key={c.key} className={cn('px-3 py-3 text-xs', c.hideOnMobile && 'max-md:hidden', c.className)}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
      {loading && (
        <div className={cn('flex items-center justify-center text-brand', rows?.length ? 'absolute inset-0 bg-white/60' : 'py-10')}>
          <Spinner className="size-6" />
        </div>
      )}
      {!loading && rows?.length === 0 && (empty ?? <EmptyState />)}
    </div>
  );
}
